import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Stack, Chip, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FLOCK_COLORS } from './Board.component';

export type SimConfig = {
  fishCounts: [number, number, number]; // [cyan, orange, green]
  predatorCount: number;
  mapWidth: number;
  mapHeight: number;
  walls: [number, number, number, number][];
};

type Props = {
  onStart: (config: SimConfig) => void;
};

const CyanFishIcon = ({ color, size = 10 }: { color: string; size?: number }) => (
  <svg width={size * 2.4} height={size * 1.8} viewBox="-2 -10 26 20" style={{ display: 'block' }}>
    <polygon points="22,0 7,-5.5 0,0 7,5.5" fill={color} />
    <polygon points="11,-5.5 14,-9.5 17,-5.5" fill={color} />
    <circle cx="16" cy="-1.5" r="2" fill="white" />
    <circle cx="16.5" cy="-1.5" r="1.1" fill="#001a33" />
  </svg>
);

const OrangeFishIcon = ({ color, size = 10 }: { color: string; size?: number }) => (
  <svg width={size * 2.4} height={size * 2.2} viewBox="-2 -12 26 24" style={{ display: 'block' }}>
    <polygon points="22,0 5,-7 0,0 5,7" fill={color} />
    <polygon points="11,6.5 8.5,11.5 3.5,6.5" fill={color} />
    <polygon points="7,-7 10,-11 14,-7" fill={color} />
    <circle cx="16" cy="-1.8" r="2" fill="white" />
    <circle cx="16.5" cy="-1.8" r="1.1" fill="#001a33" />
  </svg>
);

const GreenFishIcon = ({ color, size = 10 }: { color: string; size?: number }) => (
  <svg width={size * 2.4} height={size * 2} viewBox="-2 -11 26 22" style={{ display: 'block' }}>
    <polygon points="22,0 14,-3.5 4,-5.5 0,0 4,5.5 14,3.5" fill={color} />
    <polygon points="14,-3.5 17,-8 13,-3.8 11.5,-7.5 9,-4.5" fill={color} />
    <polygon points="11,5 9.5,9 6,5" fill={color} />
    <circle cx="17" cy="-1.5" r="2" fill="white" />
    <circle cx="17.5" cy="-1.5" r="1.1" fill="#001a33" />
  </svg>
);

const PredatorIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size * 2.8} height={size * 2.2} viewBox="-4 -13 30 26" style={{ display: 'block' }}>
    <polygon points="24,0 6,-6.5 -2,-2 -2,2 6,6.5" fill="#ff1744" />
    <polygon points="-2,-2 -8,-6 -2,0 -8,6 -2,2" fill="#b71c1c" />
    <polygon points="9,-6.5 13,-12 18,-6.5" fill="#b71c1c" />
    <polygon points="24,0 26,-2.5 22,-0.5" fill="white" />
    <polygon points="21.5,0 23.5,-2.5 19.5,-0.5" fill="white" />
    <circle cx="18" cy="-2.5" r="2.5" fill="#ffe600" />
    <circle cx="18.5" cy="-2.5" r="1.4" fill="#000" />
  </svg>
);

const FoodIcon = () => (
  <svg width={18} height={18} viewBox="0 0 18 18" style={{ display: 'block' }}>
    <circle cx="9" cy="9" r="7" fill="#76ff03" opacity="0.85" />
    <circle cx="9" cy="9" r="3.5" fill="#a5ff3c" opacity="0.6" />
  </svg>
);

const WallIcon = () => (
  <svg width={42} height={14} viewBox="0 0 42 14" style={{ display: 'block' }}>
    <line x1="3" y1="7" x2="39" y2="7" stroke="#607d8b" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

type SpeciesCounts = { cyan: number; orange: number; green: number; pred: number };

const SPECIES = [
  { key: 'cyan' as const, label: 'Cyan', color: FLOCK_COLORS[0], icon: (c: string, s: number) => <CyanFishIcon color={c} size={s} />, max: 300, step: 10 },
  { key: 'orange' as const, label: 'Orange', color: FLOCK_COLORS[1], icon: (c: string, s: number) => <OrangeFishIcon color={c} size={s} />, max: 300, step: 10 },
  { key: 'green' as const, label: 'Green', color: FLOCK_COLORS[2], icon: (c: string, s: number) => <GreenFishIcon color={c} size={s} />, max: 300, step: 10 },
  { key: 'pred' as const, label: 'Pred', color: '#ff1744', icon: (_c: string, s: number) => <PredatorIcon size={s} />, max: 20, step: 1 },
];

const SetupScreen = ({ onStart }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mapWidth = 1000;
  const mapHeight = 1000;
  const [walls, setWalls] = useState<[number, number, number, number][]>([]);
  const [species, setSpecies] = useState<SpeciesCounts>({ cyan: 50, orange: 50, green: 0, pred: 0 });
  const drawingRef = useRef<{ startX: number; startY: number } | null>(null);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const animRef = useRef<number>(0);

  const adjust = (key: keyof SpeciesCounts, delta: number, max: number) => {
    setSpecies(prev => ({ ...prev, [key]: Math.max(0, Math.min(max, prev[key] + delta)) }));
  };

  const toSim = (canvas: HTMLCanvasElement, cx: number, cy: number): [number, number] => [
    (cx / canvas.width) * mapWidth,
    (cy / canvas.height) * mapHeight,
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    const resize = () => {
      const parent = canvas.parentElement!;
      const scale = Math.min(parent.clientWidth / mapWidth, parent.clientHeight / mapHeight);
      canvas.width = Math.floor(mapWidth * scale);
      canvas.height = Math.floor(mapHeight * scale);
    };
    resize();
    const ro = new ResizeObserver(() => requestAnimationFrame(resize));
    ro.observe(canvas.parentElement!);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      const sx = w / mapWidth;
      const sy = h / mapHeight;

      ctx.fillStyle = '#0a1628';
      ctx.fillRect(0, 0, w, h);

      // Grid
      ctx.strokeStyle = '#1a2a3a';
      ctx.lineWidth = 1;
      for (let i = 0; i <= w; i += 100 * sx) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke();
      }
      for (let j = 0; j <= h; j += 100 * sy) {
        ctx.beginPath(); ctx.moveTo(0, j); ctx.lineTo(w, j); ctx.stroke();
      }

      // Completed walls
      ctx.strokeStyle = '#607d8b';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      for (const [x1, y1, x2, y2] of walls) {
        ctx.beginPath();
        ctx.moveTo(x1 * sx, y1 * sy);
        ctx.lineTo(x2 * sx, y2 * sy);
        ctx.stroke();
      }

      // In-progress wall preview
      if (drawingRef.current && mouseRef.current) {
        const { startX, startY } = drawingRef.current;
        const { x: mx, y: my } = mouseRef.current;
        ctx.strokeStyle = '#ffffff55';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(mx, my);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Instruction text
      if (walls.length === 0 && !drawingRef.current) {
        ctx.fillStyle = '#ffffff66';
        ctx.font = `${Math.floor(Math.max(14, 30 * sx))}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillText('Click and drag to draw walls', w / 2, h / 2);
      }

      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animRef.current); ro.disconnect(); };
  }, [walls]);

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    drawingRef.current = { startX: cx, startY: cy };
    mouseRef.current = { x: cx, y: cy };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const ex = e.clientX - rect.left;
    const ey = e.clientY - rect.top;
    const [sx1, sy1] = toSim(canvas, drawingRef.current.startX, drawingRef.current.startY);
    const [sx2, sy2] = toSim(canvas, ex, ey);
    const dx = sx2 - sx1;
    const dy = sy2 - sy1;
    if (Math.sqrt(dx * dx + dy * dy) > 15) {
      setWalls(prev => [...prev, [sx1, sy1, sx2, sy2]]);
    }
    drawingRef.current = null;
    mouseRef.current = null;
  };

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      drawingRef.current = null;
      mouseRef.current = null;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const totalFish = species.cyan + species.orange + species.green + species.pred;

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#0a1628', overflow: 'hidden' }}>
      {/* Top bar */}
      <Box sx={{
        px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap',
        bgcolor: '#0d1f35', borderBottom: '1px solid #1e3a5f', flexShrink: 0,
      }}>
        <Typography variant="h6" sx={{ color: '#00e5ff', fontFamily: 'monospace', letterSpacing: 2, flexShrink: 0 }}>
          BOIDS
        </Typography>

        {/* Species counters */}
        {SPECIES.map(({ key, label, color, icon, max, step }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            {icon(color, 9)}
            <Tooltip title={`${label} fish: ${species[key]}`} placement="bottom">
              <IconButton size="small" onClick={() => adjust(key, -step, max)} sx={{ color: '#607d8b', p: 0.25, minWidth: 0 }}>
                <RemoveIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Typography sx={{ color: '#90a4ae', fontSize: 12, fontFamily: 'monospace', minWidth: 26, textAlign: 'center' }}>
              {species[key]}
            </Typography>
            <Tooltip title={`Add ${label.toLowerCase()} fish`} placement="bottom">
              <IconButton size="small" onClick={() => adjust(key, step, max)} sx={{ color, p: 0.25, minWidth: 0 }}>
                <AddIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Typography sx={{ color: '#607d8b', fontSize: 11, fontFamily: 'monospace', flexShrink: 0 }}>
          = {totalFish}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Chip
            label={`${walls.length} wall${walls.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ color: '#90a4ae', borderColor: '#1e3a5f', fontSize: 12 }}
            variant="outlined"
          />
          {walls.length > 0 && (
            <Button size="small" onClick={() => setWalls([])}
              sx={{ color: '#ff9800', fontSize: 12, textTransform: 'none' }}>
              Clear
            </Button>
          )}
        </Stack>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            variant="contained"
            onClick={() => onStart({
              fishCounts: [species.cyan, species.orange, species.green],
              predatorCount: species.pred,
              mapWidth,
              mapHeight,
              walls,
            })}
            sx={{
              bgcolor: '#00e5ff', color: '#0a1628', fontWeight: 700,
              '&:hover': { bgcolor: '#29b6f6' },
            }}
          >
            Start ▶
          </Button>
          <a href="https://www.buymeacoffee.com/josueagbekodo" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" alt="Buy Me A Coffee" style={{ height: 32 }} />
          </a>
        </Box>
      </Box>

      {/* Main area: description | canvas | captions */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: simulation description */}
        <Box sx={{ width: 200, flexShrink: 0, px: 2, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', borderRight: '1px solid #1e3a5f' }}>
          <Typography sx={{ color: '#00e5ff', fontFamily: 'monospace', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
            ABOUT BOIDS
          </Typography>
          <Typography sx={{ color: '#90a4ae', fontSize: 12, lineHeight: 1.6 }}>
            Boids is a classic flocking simulation where simple local rules produce complex emergent group behaviour.
          </Typography>
          <Box>
            <Typography sx={{ color: '#607d8b', fontSize: 10, fontFamily: 'monospace', mb: 0.75 }}>FLOCKING RULES</Typography>
            {[
              { label: 'Separation', desc: 'Avoid crowding nearby boids' },
              { label: 'Alignment', desc: "Steer toward neighbors' heading" },
              { label: 'Cohesion', desc: 'Move toward the flock center' },
            ].map(({ label, desc }) => (
              <Box key={label} sx={{ mb: 1 }}>
                <Typography sx={{ color: '#00e5ff', fontSize: 11, fontFamily: 'monospace' }}>{label}</Typography>
                <Typography sx={{ color: '#607d8b', fontSize: 11, lineHeight: 1.4 }}>{desc}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: '#90a4ae', fontSize: 11, lineHeight: 1.6 }}>
            Fish flock with their own species, avoid walls, and rush toward food. Predators hunt the nearest fish.
          </Typography>
          <Typography sx={{ color: '#607d8b', fontSize: 11, lineHeight: 1.5 }}>
            The map wraps around — fish that swim off one edge reappear on the other.
          </Typography>
        </Box>

        {/* Center: canvas */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, overflow: 'hidden' }}>
          <canvas
            ref={canvasRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            style={{ cursor: 'crosshair', display: 'block' }}
          />
        </Box>

        {/* Right: entity captions */}
        <Box sx={{ width: 200, flexShrink: 0, px: 2, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75, overflowY: 'auto', borderLeft: '1px solid #1e3a5f' }}>
          <Typography sx={{ color: '#00e5ff', fontFamily: 'monospace', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
            ENTITIES
          </Typography>
          {[
            { icon: <CyanFishIcon color={FLOCK_COLORS[0]} size={12} />, label: 'Cyan Fish', desc: 'Streamlined schooling fish with dorsal fin' },
            { icon: <OrangeFishIcon color={FLOCK_COLORS[1]} size={12} />, label: 'Orange Fish', desc: 'Rounder fish with pectoral fin' },
            { icon: <GreenFishIcon color={FLOCK_COLORS[2]} size={12} />, label: 'Green Fish', desc: 'Angular fish with spiky dorsal' },
            { icon: <PredatorIcon size={12} />, label: 'Predator', desc: 'Hunts the nearest fish' },
            { icon: <FoodIcon />, label: 'Food', desc: 'Attracts nearby fish when placed' },
            { icon: <WallIcon />, label: 'Wall', desc: 'Obstacle — boids avoid it' },
          ].map(({ icon, label, desc }) => (
            <Box key={label}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                {icon}
                <Typography sx={{ color: '#e0e0e0', fontSize: 12, fontFamily: 'monospace' }}>{label}</Typography>
              </Box>
              <Typography sx={{ color: '#607d8b', fontSize: 11, lineHeight: 1.4, pl: 0.5 }}>{desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SetupScreen;
